import { z } from 'zod';
export const AuditActorSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
});
// Zod schemas for validation
/**
 * Custom Zod schema for Timestamp validation
 */
const TimestampSchema = z.object({
    seconds: z.number(),
    nanoseconds: z.number(),
    toDate: z.function(),
    toMillis: z.function(),
});
/**
 * Schema for validating audit actions
 */
export const AuditActionSchema = z.enum(['create', 'update', 'delete', 'approve', 'reject']);
/**
 * Schema for validating audit resource types
 */
export const AuditResourceTypeSchema = z.enum(['submission', 'assignment', 'cycle', 'comment']);
/**
 * Schema for validating complete audit log data
 */
export const AuditLogSchema = z.object({
    id: z.string().min(1),
    userId: z.string().min(1),
    userName: z.string().min(1),
    action: AuditActionSchema,
    resourceType: AuditResourceTypeSchema,
    resourceId: z.string().min(1),
    changes: z.record(z.string(), z.any()).optional(),
    timestamp: TimestampSchema,
    cycleId: z.string().min(1),
});
/**
 * Schema for validating audit log creation data
 */
export const CreateAuditLogDataSchema = z.object({
    userId: z.string().min(1),
    userName: z.string().min(1),
    action: AuditActionSchema,
    resourceType: AuditResourceTypeSchema,
    resourceId: z.string().min(1),
    changes: z.record(z.string(), z.any()).optional(),
    cycleId: z.string().min(1),
});
/**
 * Schema for validating audit log summary data
 */
export const AuditLogSummarySchema = z.object({
    id: z.string().min(1),
    userName: z.string().min(1),
    action: AuditActionSchema,
    resourceType: AuditResourceTypeSchema,
    resourceId: z.string().min(1),
    timestamp: TimestampSchema,
    changesSummary: z.string().optional(),
});
/**
 * Schema for validating audit log filter criteria
 */
export const AuditLogFilterSchema = z.object({
    userId: z.string().min(1).optional(),
    action: AuditActionSchema.optional(),
    resourceType: AuditResourceTypeSchema.optional(),
    resourceId: z.string().min(1).optional(),
    cycleId: z.string().min(1).optional(),
    startDate: TimestampSchema.optional(),
    endDate: TimestampSchema.optional(),
});
/**
 * Schema for validating change details
 */
export const ChangeDetailSchema = z.object({
    field: z.string().min(1),
    oldValue: z.any(),
    newValue: z.any(),
    changeType: z.enum(['added', 'modified', 'removed']),
});
/**
 * Schema for validating detailed audit log data
 */
export const DetailedAuditLogSchema = AuditLogSchema.extend({
    changeDetails: z.array(ChangeDetailSchema),
    fieldsChanged: z.number().min(0),
});
/**
 * Schema for validating audit statistics
 */
export const AuditStatsSchema = z.object({
    totalEntries: z.number().min(0),
    actionBreakdown: z.record(AuditActionSchema, z.number().min(0)),
    resourceBreakdown: z.record(AuditResourceTypeSchema, z.number().min(0)),
    topUsers: z.array(z.object({
        userId: z.string().min(1),
        userName: z.string().min(1),
        actionCount: z.number().min(0),
    })),
    dateRange: z.object({
        start: TimestampSchema,
        end: TimestampSchema,
    }),
});
/**
 * Type guards for audit data
 */
export const isValidAuditLog = (data) => {
    return AuditLogSchema.safeParse(data).success;
};
export const isValidAuditAction = (action) => {
    return AuditActionSchema.safeParse(action).success;
};
export const isValidAuditResourceType = (resourceType) => {
    return AuditResourceTypeSchema.safeParse(resourceType).success;
};
export const isValidCreateAuditLogData = (data) => {
    return CreateAuditLogDataSchema.safeParse(data).success;
};
export const isValidAuditLogSummary = (data) => {
    return AuditLogSummarySchema.safeParse(data).success;
};
export const isValidAuditLogFilter = (data) => {
    return AuditLogFilterSchema.safeParse(data).success;
};
export const isValidChangeDetail = (data) => {
    return ChangeDetailSchema.safeParse(data).success;
};
export const isValidDetailedAuditLog = (data) => {
    return DetailedAuditLogSchema.safeParse(data).success;
};
export const isValidAuditStats = (data) => {
    return AuditStatsSchema.safeParse(data).success;
};
